import React from 'react';
import { DayPicker, Matcher } from 'react-day-picker';
import { de } from 'date-fns/locale';
import classNames from 'classnames';
import 'react-day-picker/dist/style.css';
import './Calendar.less';

interface Props {
    /** Selected day as a LOCAL-midnight Date (the DatePicker boundary converts Berlin → local). */
    selected: Date;
    /** Month to render initially (local-midnight Date). */
    defaultMonth: Date;
    /** Called with the clicked day as a LOCAL-midnight Date. */
    onSelect: (day: Date) => void;
    /** Days NOT matched here are disabled / not clickable. */
    disabled: Matcher | Matcher[];
    /** Navigation bounds (local-midnight Dates) — first/last selectable month. */
    fromMonth: Date;
    toMonth: Date;
    /** Persistent hint shown below the grid, e.g. "Auswahl nur vom 29.05. bis 09.06.2026". */
    footerHint?: string;
    /** Stretch the calendar to fill its container width (used inside the mobile full-screen Dialog). */
    fullWidth?: boolean;
    className?: string;
}

/**
 * Pure calendar grid (react-day-picker wrapper). Knows nothing about timezones or the
 * allow-list logic — it works purely in local-time Date objects. The owning DatePicker
 * pins everything to Berlin at its boundary and hands down already-converted props.
 *
 *   DatePicker (Berlin pin)  →  Calendar (local-time DayPicker)  →  onSelect(localDay)  →  DatePicker (→ Berlin)
 */
const Calendar = ({ selected, defaultMonth, onSelect, disabled, fromMonth, toMonth, footerHint, fullWidth, className }: Props) => {
    return (
        <DayPicker
            className={classNames('envago-calendar', { 'envago-calendar--full-width': fullWidth }, className)}
            mode="single"
            required
            locale={de}
            weekStartsOn={1}
            fixedWeeks={false}
            showOutsideDays
            selected={selected}
            defaultMonth={defaultMonth}
            fromMonth={fromMonth}
            toMonth={toMonth}
            disabled={disabled}
            onSelect={(day) => {
                // `required` + mode="single" → `day` is always a Date on a real selection.
                if (day) onSelect(day);
            }}
            footer={footerHint ? <p className={'envago-calendar__footer'}>{footerHint}</p> : undefined}
        />
    );
};

export default Calendar;
